package top.wcpe.allincore.ui.runtime.host

import top.wcpe.allincore.ui.runtime.internal.concurrent.AtomicBooleanFlag
import top.wcpe.allincore.ui.runtime.internal.concurrent.newConcurrentSet
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.internal.time.currentTimeMillis
import top.wcpe.allincore.ui.runtime.ir.DslExecutor
import top.wcpe.allincore.ui.runtime.ir.ScheduledTaskHandle
import kotlin.concurrent.Volatile

/**
 * Session 级定时器宿主：管理 `timer.timeout / timer.interval` 创建的所有 handle。
 *
 * 所有任务共用 session 的 [DslExecutor]，与 DSL 主流程串行；session 销毁路径调 [dispose] 时，
 * 所有未取消的 handle 都会被 cancel，DSL 端不需要手动 cancel。
 *
 * 暂停/恢复：[pause] 取消所有活跃定时任务但保留注册信息（类型、延迟、周期、任务），[resume] 按注册信息
 * 重新调度，并扣除已过去的时长。timeout 类型如果暂停前已过期则不再恢复；interval 类型恢复后从剩余延迟处继续。
 *
 * 线程安全：[active] 用并发 Set（JVM=ConcurrentHashMap.newKeySet）保证并发添加 / 移除安全。
 */
class TimerHost(private val executor: DslExecutor) {

    private val active: MutableSet<HandleImpl> = newConcurrentSet()

    @Volatile
    private var disposed = false

    /** 抑制标志：暂停期间拒绝新的定时任务提交。 */
    @Volatile
    private var suppressed = false

    /** 暂停时快照的定时器记录，resume 时用于重新调度。 */
    private val pausedRecords = ArrayList<TimerRecord>()

    /** 本次暂停的时刻（currentTimeMillis），resume 时计算已过去的时长。 */
    @Volatile
    private var pausedAtMs = -1L

    /** 一次性延时：delayMs 毫秒后执行 task；handle.cancel() 阻断尚未触发的执行。 */
    fun timeout(delayMs: Long, task: UiTask): TimerHandle {
        if (disposed || suppressed) {
            return CancelledHandle
        }
        val handle = HandleImpl()
        handle.record = TimerRecord(TimerType.TIMEOUT, delayMs, 0L, task, currentTimeMillis())
        val wrapped = UiTask {
            if (handle.isCancelled() || disposed) {
                return@UiTask
            }
            try {
                task.run()
            } catch (t: Throwable) {
                LOGGER.warn("[Timer] timeout 任务异常（已拦截）", t)
            } finally {
                // 一次性触发完毕：从 active 移除并标记 cancelled，让 isCancelled() 反馈"已结束"
                active.remove(handle)
                handle.markFinished()
            }
        }
        val future = executor.schedule(wrapped, maxOf(0L, delayMs)) ?: return CancelledHandle
        handle.future = future
        active.add(handle)
        return handle
    }

    /** 周期性：首次延迟 = period（与 setInterval 同款），之后每 period 触发一次。 */
    fun interval(periodMs: Long, task: UiTask): TimerHandle = interval(periodMs, periodMs, task)

    /** 周期性：自定义首次延迟与周期。 */
    fun interval(initialDelayMs: Long, periodMs: Long, task: UiTask): TimerHandle {
        if (disposed || suppressed) {
            return CancelledHandle
        }
        val handle = HandleImpl()
        handle.record = TimerRecord(TimerType.INTERVAL, initialDelayMs, periodMs, task, currentTimeMillis())
        val wrapped = UiTask {
            if (handle.isCancelled() || disposed) {
                return@UiTask
            }
            try {
                task.run()
            } catch (t: Throwable) {
                LOGGER.warn("[Timer] interval 任务异常（已拦截，下一次周期不受影响）", t)
            }
        }
        val future = executor.scheduleAtFixedRate(wrapped, initialDelayMs, periodMs) ?: return CancelledHandle
        handle.future = future
        active.add(handle)
        return handle
    }

    /**
     * Session 销毁时调用：cancel 所有尚未触发 / 周期性任务，且屏蔽后续 timeout / interval 提交。幂等。
     */
    fun dispose() {
        disposed = true
        // 快照遍历：cancel() 会从 active 自移除，避免遍历中修改（JS 端 LinkedHashSet 不容忍）
        for (h in active.toList()) {
            try {
                h.cancel()
            } catch (t: Throwable) {
                LOGGER.warn("[Timer] dispose 时取消句柄异常（已拦截）", t)
            }
        }
        active.clear()
        pausedRecords.clear()
    }

    /**
     * 暂停：快照所有活跃定时器的注册信息，取消底层 future，开启抑制模式。
     * resume 后按快照重新调度，timeout 扣除已过时长，interval 从剩余延迟处继续。
     */
    fun pause() {
        if (suppressed) {
            return
        }
        suppressed = true
        pausedAtMs = currentTimeMillis()
        // 快照当前活跃定时器的注册信息（已取消或已完成的不记录）
        pausedRecords.clear()
        for (h in ArrayList(active)) {
            val rec = h.record
            if (!h.isCancelled() && rec != null) {
                pausedRecords.add(rec.withPauseTime(pausedAtMs))
            }
            try {
                h.cancel()
            } catch (ignored: Throwable) {
                // 忽略
            }
        }
        active.clear()
    }

    /**
     * 恢复：按暂停时的快照重新调度定时器，扣除已过去的时长。
     * timeout 已过期的不再恢复；interval 从剩余初始延迟处继续。
     */
    fun resume() {
        if (!suppressed) {
            return
        }
        suppressed = false
        val now = currentTimeMillis()
        // pausedAtMs 由 pause() 记录，与 TimerRecord.pauseTimeMs 等价，统一用 elapsed 计算
        val elapsed = if (pausedAtMs >= 0) now - pausedAtMs else 0L
        for (rec in pausedRecords) {
            if (rec.type == TimerType.TIMEOUT) {
                val remaining = rec.delayMs - elapsed
                if (remaining > 0) {
                    timeout(remaining, rec.task)
                }
                // remaining <= 0 表示暂停期间已过期，不再触发
            } else {
                // interval：计算首次触发的剩余延迟
                val remaining = (rec.initialDelayMs - elapsed).coerceAtLeast(0L)
                interval(remaining, rec.periodMs, rec.task)
            }
        }
        pausedRecords.clear()
        pausedAtMs = -1L
    }

    /** 查询是否处于抑制状态。 */
    fun isSuppressed(): Boolean = suppressed

    // ---- 内部类型 ----

    private enum class TimerType { TIMEOUT, INTERVAL }

    /** 暂停时保存的定时器注册信息，用于 resume 时重新调度。 */
    private class TimerRecord(
        val type: TimerType,
        val delayMs: Long,
        val periodMs: Long,
        val task: UiTask,
        val createdAtMs: Long,
        val pauseTimeMs: Long = createdAtMs,
    ) {
        /** 与 delayMs 等价（历史双命名：TIMEOUT 读 delayMs，INTERVAL 读 initialDelayMs）。 */
        val initialDelayMs: Long get() = delayMs

        /** 创建带暂停时刻的副本。 */
        fun withPauseTime(pauseTimeMs: Long): TimerRecord =
            TimerRecord(type, delayMs, periodMs, task, createdAtMs, pauseTimeMs)
    }

    /** 已取消的占位句柄；executor 关闭或 host disposed 时返回。 */
    private object CancelledHandle : TimerHandle {
        override fun cancel() = Unit
        override fun isCancelled(): Boolean = true
    }

    /** 普通句柄实现：包 ScheduledTaskHandle + cancelled 标志。 */
    private inner class HandleImpl : TimerHandle {
        @Volatile
        var future: ScheduledTaskHandle? = null

        @Volatile
        var record: TimerRecord? = null

        private val cancelled = AtomicBooleanFlag(false)

        override fun cancel() {
            if (cancelled.compareAndSet(false, true)) {
                future?.cancel()
                active.remove(this)
            }
        }

        /** 一次性 timeout 触发完成后调用：标记为已完成（通过 cancelled 表示"不可再触发"）。 */
        fun markFinished() {
            cancelled.set(true)
        }

        override fun isCancelled(): Boolean = cancelled.get()
    }

    companion object {
        private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.host.TimerHost")
    }
}
